import { createSlice } from "@reduxjs/toolkit";
import {
  calculateArea,
  calculateWindowDimensions,
  countPolygonEdges,
  findWindowAreaCluster,
  findWindowDimensionCluster,
  getPolygonTypeName,
} from "../components/utility/dataManipulation";

const initialState = {
  allWindows: [],
  groupedWindows: [],
  resultArray: [],
  isLoaded: false,
  filters: {
    floor: [],
    name: "",
    shapeSize: [],
    area: [],
  },
  filteredWindows: [],
  areaClusters: [],
  dimensionClusters: [],
};

const windowDataSlice = createSlice({
  name: "windowData",
  initialState,
  reducers: {
    setWindowData: (state, action) => {
      const { resultArray, groupedWindows } = action.payload;

      const allWindows = [];
      resultArray.forEach((floorWindows, floorIndex) => {
        floorWindows.forEach((window, windowIndex) => {
          allWindows.push({
            ...window,
            floorIndex,
            windowIndex,
            displayName: `${
              window.name || `Window ${windowIndex + 1}`
            } (Floor ${floorIndex + 1})`,
          });
        });
      });

      state.allWindows = allWindows;
      state.groupedWindows = groupedWindows;
      state.resultArray = resultArray;
      state.isLoaded = true;
    },

    clearWindowData: (state) => {
      state.allWindows = [];
      state.groupedWindows = [];
      state.resultArray = [];
      state.isLoaded = false;
      state.filters = {
        floor: [],
        name: "",
        shapeSize: [],
        area: [],
      };
      state.filteredWindows = [];
    },

    setWindowFilters: (state, action) => {
      state.filters = action.payload;
      state.filteredWindows = state.allWindows.filter((window) => {
        const filters = action.payload;
        if (
          filters.floor.length > 0 &&
          !filters.floor.includes(window.floorIndex)
        ) {
          return false;
        }

        if (
          filters.name &&
          !window.name?.toLowerCase().includes(filters.name.toLowerCase())
        ) {
          return false;
        }

        if (filters.shapeSize.length > 0 && window.polygon) {
          const edgeCount = countPolygonEdges(window.polygon);

          if (edgeCount === 4) {
            const { width, height } = calculateWindowDimensions(window.polygon);

            if (state.dimensionClusters.length > 0) {
              const matchingCluster = findWindowDimensionCluster(
                width,
                height,
                state.dimensionClusters
              );
              if (
                matchingCluster &&
                !filters.shapeSize.includes(matchingCluster.label)
              ) {
                return false;
              }
            }
          } else {
            const polygonType = getPolygonTypeName(edgeCount);
            const polygonLabel = `${polygonType} (${edgeCount} edges)`;

            if (!filters.shapeSize.includes(polygonLabel)) {
              return false;
            }
          }
        }

        if (filters?.area?.length > 0 && window?.polygon) {
          const windowArea = calculateArea(window?.polygon);

          if (state?.areaClusters?.length > 0) {
            const matchingCluster = findWindowAreaCluster(
              windowArea,
              state.areaClusters
            );
            if (
              !matchingCluster ||
              !filters?.area?.includes(matchingCluster?.centroid)
            ) {
              return false;
            }
          }
        }

        return true;
      });
    },

    clearWindowFilters: (state) => {
      state.filters = {
        floor: [],
        name: "",
        shapeSize: [],
        area: [],
      };
      state.filteredWindows = [...state.allWindows];
    },

    setClusterData: (state, action) => {
      const { areaClusters, dimensionClusters } = action.payload;
      state.areaClusters = areaClusters || [];
      state.dimensionClusters = dimensionClusters || [];
    },
  },
});

export const {
  setWindowData,
  clearWindowData,
  setWindowFilters,
  clearWindowFilters,
  setClusterData,
} = windowDataSlice.actions;

export default windowDataSlice.reducer;
